import {
  createTheme,
  MantineColorShade,
  MantineColorsTuple,
  DEFAULT_THEME,
} from "@mantine/core";
import { lighten, darken } from "@mantine/core";
import { useActiveUserPartner } from "../store/selectors";
import {
  advizeBlue,
  advizeGreen,
  allianceGreen,
  allianceOrange,
  asuGold,
  asuMaroon,
  berkeleyPrimary,
  berkeleySecondary,
  californiaLutheranPrimary,
  californiaLutheranSecondary,
  compassDiversifiedPrimary,
  compassDiversifiedSecondary,
  hericansPrimary,
  hericansSecondary,
  knaussPrimary,
  knaussSecondary,
  mindsMatterPrimary,
  mindsMatterSecondary,
  sacStateGold,
  sacStateGreen,
  slate,
  uclaAndersonPrimary,
  uclaAndersonSecondary,
  stanfordPrimary,
  stanfordSecondary,
  stanfordAccent1,
  stanfordAccent2,
  stanfordAccent3,
  stanfordAccent4,
  stanfordAccent5,
  uscaSecondary,
  uscaPrimary,
  arizonaEllerPrimary,
  arizonaEllerSecondary,
  purduePrimary,
  purdueSecondary,
  uvaDardenPrimary,
  uvaDardenSecondary,
  sierraCollegePrimary,
  sierraCollegeSecondary,
  jessupUniversityPrimary,
  jessupUniversitySecondary,
  highlightYellow,
  pbaPrimary,
  pbaSecondary,
  calStateLongBeachSecondary,
  calStateLongBeachPrimary,
  lawhubPrimary,
  lawhubSecondary,
  cwuSecondary,
  cwuPrimary,
  smuPrimary,
  smuSecondary,
  wbuSecondary,
  wbuPrimary,
  utkHaslamSecondary,
  utkHaslamPrimary,
  unlvSecondary,
  unlvPrimary,
  foreseeableFuturesPrimary,
  foreseeableFuturesSecondary,
  eagleAcademyFoundationPrimary,
  eagleAcademyFoundationSecondary,
  northshorePrimary,
  northshoreSecondary,
  miamiSecondary,
  miamiPrimary,
  pittSecondary,
  pittPrimary,
  yorkCollegeCunySecondary,
  yorkCollegeCunyPrimary,
  pointLomaSecondary,
  pointLomaPrimary,
  ncStatePrimary,
  ncStateSecondary,
  scuSecondary,
  scuPrimary,
  adobeSecondary,
  adobePrimary,
  uscSecondary,
  uscPrimary,
  chicagoBoothSecondary,
  chicagoBoothPrimary,
} from "./colors";
import { useStore } from "@/store";
import { useMemo } from "react";
import { useIsDemoApp } from "@/store/selectors/featureSelectors";

type IThemeOverride = {
  primaryShade: MantineColorShade;
  colors: {
    primary: MantineColorsTuple;
    secondary: MantineColorsTuple;
    background: MantineColorsTuple;
    border: MantineColorsTuple;
    text: MantineColorsTuple;
    loginBackground?: MantineColorsTuple;
    accentColor1?: MantineColorsTuple;
    accentColor2?: MantineColorsTuple;
    accentColor3?: MantineColorsTuple;
    accentColor4?: MantineColorsTuple;
    accentColor5?: MantineColorsTuple;
  };
};

const advizeTheme: IThemeOverride = {
  primaryShade: 7,
  colors: {
    primary: advizeBlue,
    secondary: advizeGreen,
    background: slate,
    border: slate,
    text: slate,
  },
};

const schoolThemes: Record<string, IThemeOverride> = {
  ["sacramento-state"]: {
    primaryShade: 8,
    colors: {
      primary: sacStateGreen,
      secondary: sacStateGold,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["asu-w.p.-carey-school-of-business"]: {
    primaryShade: 9,
    colors: {
      primary: asuMaroon,
      secondary: asuGold,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["alliance-college-ready-public-schools"]: {
    primaryShade: 7,
    colors: {
      primary: allianceGreen,
      secondary: allianceOrange,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["california-lutheran-university"]: {
    primaryShade: 7,
    colors: {
      primary: californiaLutheranPrimary,
      secondary: californiaLutheranSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["compass-diversified"]: {
    primaryShade: 7,
    colors: {
      primary: compassDiversifiedPrimary,
      secondary: compassDiversifiedSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["hericanes"]: {
    primaryShade: 6,
    colors: {
      primary: hericansPrimary,
      secondary: hericansSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["minds-matter"]: {
    primaryShade: 6,
    colors: {
      primary: mindsMatterPrimary,
      secondary: mindsMatterSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["uc-berkeley---social-sciences"]: {
    primaryShade: 8,
    colors: {
      primary: berkeleyPrimary,
      secondary: berkeleySecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["ucla-anderson"]: {
    primaryShade: 9,
    colors: {
      primary: uclaAndersonPrimary,
      secondary: uclaAndersonSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["usd-knauss-school-of-business"]: {
    primaryShade: 7,
    colors: {
      primary: knaussPrimary,
      secondary: knaussSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["stanford-eng-comp-forum"]: {
    primaryShade: 8,
    colors: {
      primary: stanfordPrimary,
      secondary: stanfordSecondary,
      background: slate,
      border: slate,
      text: slate,
      accentColor1: stanfordAccent1,
      accentColor2: stanfordAccent2,
      accentColor3: stanfordAccent3,
      accentColor4: stanfordAccent4,
      accentColor5: stanfordAccent5,
    },
  },
  ["usca"]: {
    primaryShade: 7,
    colors: {
      primary: uscaPrimary,
      secondary: uscaSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["university-of-arizona"]: {
    primaryShade: 7,
    colors: {
      primary: arizonaEllerPrimary,
      secondary: arizonaEllerSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["university-of-arizona---eller-college-of-management"]: {
    primaryShade: 7,
    colors: {
      primary: arizonaEllerPrimary,
      secondary: arizonaEllerSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["purdue-university-mitch-daniels-school-of-business"]: {
    primaryShade: 7,
    colors: {
      primary: purduePrimary,
      secondary: purdueSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["uva-darden-school-of-business"]: {
    primaryShade: 8,
    colors: {
      primary: uvaDardenPrimary,
      secondary: uvaDardenSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["sierra-college"]: {
    primaryShade: 7,
    colors: {
      primary: sierraCollegePrimary,
      secondary: sierraCollegeSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["jessup-university"]: {
    primaryShade: 7,
    colors: {
      primary: jessupUniversityPrimary,
      secondary: jessupUniversitySecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["palm-beach-atlantic-university"]: {
    primaryShade: 7,
    colors: {
      primary: pbaPrimary,
      secondary: pbaSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["csu-long-beach"]: {
    primaryShade: 9,
    colors: {
      primary: calStateLongBeachPrimary,
      secondary: calStateLongBeachSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["lawhub"]: {
    primaryShade: 9,
    colors: {
      primary: lawhubPrimary,
      secondary: lawhubSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["central-washington-university"]: {
    primaryShade: 9,
    colors: {
      primary: cwuPrimary,
      secondary: cwuSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["smu"]: {
    primaryShade: 8,
    colors: {
      primary: smuPrimary,
      secondary: smuSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["wbu"]: {
    primaryShade: 7,
    colors: {
      primary: wbuPrimary,
      secondary: wbuSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["utk-haslam"]: {
    primaryShade: 7,
    colors: {
      primary: utkHaslamPrimary,
      secondary: utkHaslamSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["unlv"]: {
    primaryShade: 7,
    colors: {
      primary: unlvPrimary,
      secondary: unlvSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["foreseeable-futures"]: {
    primaryShade: 7,
    colors: {
      primary: foreseeableFuturesPrimary,
      secondary: foreseeableFuturesSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["eagle-academy-foundation"]: {
    primaryShade: 7,
    colors: {
      primary: eagleAcademyFoundationPrimary,
      secondary: eagleAcademyFoundationSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },

  ["northshore"]: {
    primaryShade: 7,
    colors: {
      primary: northshorePrimary,
      secondary: northshoreSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["miami"]: {
    primaryShade: 7,
    colors: {
      primary: miamiPrimary,
      secondary: miamiSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["pitt"]: {
    primaryShade: 7,
    colors: {
      primary: pittPrimary,
      secondary: pittSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["york-college-cuny"]: {
    primaryShade: 7,
    colors: {
      primary: yorkCollegeCunyPrimary,
      secondary: yorkCollegeCunySecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["point-loma"]: {
    primaryShade: 7,
    colors: {
      primary: pointLomaPrimary,
      secondary: pointLomaSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["ncsu"]: {
    primaryShade: 7,
    colors: {
      primary: ncStatePrimary,
      secondary: ncStateSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["santa-clara-university"]: {
    primaryShade: 7,
    colors: {
      primary: scuPrimary,
      secondary: scuSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["adobe"]: {
    primaryShade: 7,
    colors: {
      primary: adobePrimary,
      secondary: adobeSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["usc"]: {
    primaryShade: 7,
    colors: {
      primary: uscPrimary,
      secondary: uscSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
  ["chicago-booth"]: {
    primaryShade: 7,
    colors: {
      primary: chicagoBoothPrimary,
      secondary: chicagoBoothSecondary,
      background: slate,
      border: slate,
      text: slate,
    },
  },
};

export const useTheme = () => {
  const partner = useActiveUserPartner();
  const demoPrimaryColor = useStore((state) => state.demo.demoPrimaryColor);
  const demoSecondaryColor = useStore((state) => state.demo.demoSecondaryColor);
  const isDemoApp = useIsDemoApp();

  const primaryTuple = useMemo(() => {
    if (!demoPrimaryColor || !isDemoApp) return null;
    return [
      lighten(demoPrimaryColor, 0.8), // Lightest - index 0
      lighten(demoPrimaryColor, 0.7),
      lighten(demoPrimaryColor, 0.5),
      lighten(demoPrimaryColor, 0.3),
      lighten(demoPrimaryColor, 0.2),
      lighten(demoPrimaryColor, 0.1),
      demoPrimaryColor, // Original color - index 6
      darken(demoPrimaryColor, 0.1),
      darken(demoPrimaryColor, 0.2),
      darken(demoPrimaryColor, 0.3), // Darkest
    ];
  }, [demoPrimaryColor, isDemoApp]);

  const secondaryTuple = useMemo(() => {
    if (!demoSecondaryColor || !isDemoApp) return null;
    return [
      lighten(demoSecondaryColor, 0.8), // Lightest - index 0
      lighten(demoSecondaryColor, 0.7),
      lighten(demoSecondaryColor, 0.5),
      lighten(demoSecondaryColor, 0.3),
      lighten(demoSecondaryColor, 0.2),
      lighten(demoSecondaryColor, 0.1),
      demoSecondaryColor, // Original color - index 6
      darken(demoSecondaryColor, 0.1),
      darken(demoSecondaryColor, 0.2),
      darken(demoSecondaryColor, 0.3), // Darkest
    ];
  }, [demoSecondaryColor, isDemoApp]);

  const themeToUse =
    schoolThemes[partner?.slug as keyof typeof schoolThemes] || advizeTheme;

  const { colors, ...rest } = themeToUse;

  const demoOverrides = useMemo(() => {
    if (!isDemoApp) return null;
    const colors: any = {};

    if (primaryTuple) {
      colors.primary = primaryTuple;
    }

    if (secondaryTuple) {
      colors.secondary = secondaryTuple;
    }

    return {
      colors,
    };
  }, [primaryTuple, secondaryTuple, isDemoApp]);

  return createTheme({
    fontFamily: "Arial,Helvetica,sans-serif",
    primaryColor: "blue",
    headings: {
      fontFamily: "League Spartan, sans-serif",
    },
    breakpoints: {
      xs: "36em",
      sm: "48em",
      md: "62em",
      lg: "75em",
      xl: "88em",
      mobileMax: "550px",
      tabletMax: "1100px",
      laptopMax: "1400px",
    },
    colors: {
      slate,
      accentColor1: DEFAULT_THEME.colors.pink, // Use Mantine's pink color tuple
      accentColor2: DEFAULT_THEME.colors.cyan,
      accentColor3: DEFAULT_THEME.colors.grape,
      accentColor4: DEFAULT_THEME.colors.orange,
      accentColor5: DEFAULT_THEME.colors.green,
      highlightYellow,
      ...colors,
      ...demoOverrides?.colors,
    },
    ...rest,
  });
};
